import { useEffect, useRef, useCallback } from 'react';
import { apiClient } from 'shared/api';

interface FormData {
  [key: string]: any;
}

interface UseFormAutoSaveOptions {
  formId: string;
  data: FormData;
  enabled?: boolean;
  saveInterval?: number; // в миллисекундах
  endpoint?: string; // кастомный эндпоинт для автосохранения
  formType?: 'select' | 'leasing'; // тип формы
}

export const useFormAutoSave = ({
  formId,
  data,
  enabled = true,
  saveInterval = 10000, // 5 секунд по умолчанию
  endpoint,
  formType = 'select',
}: UseFormAutoSaveOptions) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedDataRef = useRef<string>('');
  const isFirstLoadRef = useRef(true);

  // Получение данных из localStorage
  const getStoredData = useCallback((): FormData | null => {
    try {
      const stored = localStorage.getItem(`form_autosave_${formId}`);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Ошибка при получении данных из localStorage:', error);
      return null;
    }
  }, [formId]);

  // Сохранение данных в localStorage
  const saveToLocalStorage = useCallback(
    (formData: FormData) => {
      try {
        localStorage.setItem(`form_autosave_${formId}`, JSON.stringify(formData));
      } catch (error) {
        console.error('Ошибка при сохранении в localStorage:', error);
      }
    },
    [formId],
  );

  // Отправка данных на сервер
  const saveToServer = useCallback(
    async (formData: FormData) => {
      try {
        const formDataToSend = new FormData();

        // Преобразуем объект в FormData
        Object.entries(formData).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (typeof value === 'object' && value instanceof File) {
              formDataToSend.append(key, value);
            } else if (typeof value === 'object') {
              formDataToSend.append(key, JSON.stringify(value));
            } else {
              formDataToSend.append(key, String(value));
            }
          }
        });

        formDataToSend.append('type', formType);
        formDataToSend.append('fulfilled', 'false');

        // Определяем эндпоинт в зависимости от типа формы или используем кастомный
        const saveEndpoint =
          endpoint ||
          (formType === 'leasing'
            ? '/leasing-applications/autosave'
            : '/select-applications/autosave');

        await apiClient.post(saveEndpoint, formDataToSend);
        // console.log(`Данные автоматически сохранены на сервере (${formType})`);
      } catch (error) {
        console.error('Ошибка при автосохранении на сервере:', error);
      }
    },
    [endpoint, formType],
  );

  // Проверка изменений и сохранение
  const checkAndSave = useCallback(() => {
    if (!enabled) return;

    const currentDataString = JSON.stringify(data);

    // console.log({ currentDataString, data });

    // Проверяем, изменились ли данные с последнего сохранения
    if (currentDataString !== lastSavedDataRef.current) {
      // Сохраняем в localStorage
      saveToLocalStorage(data);

      const dataKeys = Object.keys(data);

      const hasRequiredData = ['phone'].every((val) => dataKeys.includes(val));

      // Отправляем на сервер только если форма не пустая
      const hasData = Object.values(data).some(
        (value) => value !== undefined && value !== null && value !== '',
      );

      // console.log({ hasData });

      if (hasData) {
        saveToServer(data);
      }

      lastSavedDataRef.current = currentDataString;
    }
  }, [data, enabled, saveToLocalStorage, saveToServer]);

  // Очистка автосохраненных данных
  const clearAutoSavedData = useCallback(() => {
    try {
      localStorage.removeItem(`form_autosave_${formId}`);
      lastSavedDataRef.current = '';
    } catch (error) {
      console.error('Ошибка при очистке localStorage:', error);
    }
  }, [formId]);

  // Инициализация интервала автосохранения
  useEffect(() => {
    if (!enabled) return;

    // Загружаем сохраненные данные при первой загрузке
    if (isFirstLoadRef.current) {
      // const storedData = getStoredData();
      // if (storedData) {
      //   console.log('Найдены сохраненные данные формы:', storedData);
      // }
      isFirstLoadRef.current = false;
    }

    // Запускаем интервал автосохранения
    intervalRef.current = setInterval(checkAndSave, saveInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, saveInterval, checkAndSave, getStoredData]);

  // Сохранение при размонтировании компонента
  useEffect(() => {
    return () => {
      checkAndSave();
    };
  }, [checkAndSave]);

  return {
    getStoredData,
    clearAutoSavedData,
    saveToServer: () => saveToServer(data),
  };
};
